import React, { useState, useMemo, useRef, useEffect } from 'react';
import { PromptParts } from './types';
import { FRAMEWORK_CATEGORIES, INITIAL_PROMPT_STATE, ASPECT_RATIO_OPTIONS, TEXT_MEDIUM_OPTIONS } from './constants';
import { PromptField } from './PromptField';
import { buildPromptString, parsePrompt } from './gemini';
import { copyToClipboard as clipboardCopy } from '@/utils/clipboard';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import { parseSongMarkdown } from '@/utils/parseSongMarkdown';
import { generateAlbumCoverConcepts, type AlbumCoverConcept } from '@/services/albumCoverConceptsService';
import {
  Layout,
  Copy,
  RotateCcw,
  Sparkles,
  Music,
  Eye,
  FileText,
  Hash,
  CheckCircle2,
  AlertTriangle,
  PenTool,
  UploadCloud,
  Save,
  Ratio,
  Wand2,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input, TextArea, Select } from '../ui/Form';

interface PromptArchitectAppProps {
  initialMasterInput?: string;
  draftId?: string;
}

const PromptArchitectApp: React.FC<PromptArchitectAppProps> = ({ initialMasterInput = '', draftId }) => {
  const [masterInput, setMasterInput] = useState(initialMasterInput);
  const [isParsing, setIsParsing] = useState(false);
  const [prompt, setPrompt] = useState<PromptParts>(INITIAL_PROMPT_STATE);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // "Generate 3 Variations" state — FCAA-style diverse concepts fed back through parsePrompt
  const [variations, setVariations] = useState<AlbumCoverConcept[]>([]);
  const [isVarying, setIsVarying] = useState(false);
  const [loadingVarIdx, setLoadingVarIdx] = useState<number | null>(null);

  // Load existing art prompt from draft if available
  useEffect(() => {
    if (!draftId) return;
    (async () => {
      try {
        const res = await fetchWithCsrf(`/api/drafts/${draftId}`);
        if (!res.ok) return;
        const draft = await res.json();
        if (draft.artPromptParts) {
          try {
            const parts = JSON.parse(draft.artPromptParts);
            // Merge with defaults for new fields
            setPrompt({ ...INITIAL_PROMPT_STATE, ...parts });
          } catch {}
        }
      } catch {}
    })();
  }, [draftId]);

  // Save art prompt to draft
  const handleSave = async () => {
    if (!draftId) return;
    setIsSaving(true);
    try {
      const res = await fetchWithCsrf(`/api/drafts/${draftId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          artPrompt: buildPromptString(prompt),
          artPromptParts: JSON.stringify(prompt),
        }),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch (err) {
      setError('Failed to save art prompt');
    } finally {
      setIsSaving(false);
    }
  };

  // Update masterInput when prop changes
  useEffect(() => {
    if (initialMasterInput) {
      setMasterInput(initialMasterInput);
    }
  }, [initialMasterInput]);

  const storyGroup = FRAMEWORK_CATEGORIES.slice(0, 4);
  const shotGroup = FRAMEWORK_CATEGORIES.slice(4, 7);
  const realismGroup = FRAMEWORK_CATEGORIES.slice(7, 9);
  const guardrailsGroup = FRAMEWORK_CATEGORIES.slice(9, 10);

  const finalPromptString = useMemo(() => buildPromptString(prompt), [prompt]);

  const handleFieldChange = (id: string, value: string) => {
    setPrompt(prev => ({ ...prev, [id]: value }));
  };

  const handleParse = async () => {
    if (!masterInput.trim()) return;
    setIsParsing(true);
    setError(null);
    try {
      // Fetch previous art prompt subjects for anti-clone system
      let previousSubjects: string[] = [];
      try {
        const res = await fetchWithCsrf('/api/drafts?limit=20');
        if (res.ok) {
          const drafts = await res.json();
          previousSubjects = (drafts || [])
            .filter((d: any) => d.artPromptParts && d.id !== draftId)
            .map((d: any) => {
              try { return JSON.parse(d.artPromptParts).subject; } catch { return null; }
            })
            .filter(Boolean)
            .slice(0, 10);
        }
      } catch {} // Non-critical — proceed without history

      const parsed = await parsePrompt(masterInput, previousSubjects);
      // Preserve user's aspect ratio and text medium selections
      setPrompt(prev => ({
        ...parsed,
        aspectRatio: prev.aspectRatio || parsed.aspectRatio || '1:1',
        textMedium: prev.textMedium || parsed.textMedium || '',
      }));
    } catch (err: any) {
      setError("System Parsing Error: " + (err.message || "Unknown anomaly detected"));
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.md') && !file.name.endsWith('.txt')) {
      setError("Invalid file format. Please upload a .md or .txt file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      if (typeof content === 'string') {
        setMasterInput(content);
        setError(null);
      }
    };
    reader.onerror = () => {
      setError("Failed to read file contents.");
    };
    reader.readAsText(file);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleGenerateVariations = async () => {
    if (!masterInput.trim()) return;
    setIsVarying(true);
    setError(null);
    try {
      const parsed = parseSongMarkdown(masterInput);
      const result = await generateAlbumCoverConcepts({
        title: parsed.title || 'Untitled',
        artist: parsed.artist,
        summary: parsed.summary,
        lyrics: parsed.lyrics || masterInput,
        aspectRatio: prompt.aspectRatio || '1:1',
      });
      if (result.length === 0) {
        setError('No variations returned. Try again or check your OpenAI API key.');
      }
      setVariations(result);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Variation generation failed.';
      setError(msg);
      setVariations([]);
    } finally {
      setIsVarying(false);
    }
  };

  const handleLoadVariation = async (idx: number, concept: AlbumCoverConcept) => {
    setLoadingVarIdx(idx);
    setError(null);
    try {
      // Feed the concept text through the existing parsePrompt so the fields fill
      // the same way "Execute Analysis" does — reusing PA's proven deconstruction.
      const parsed = await parsePrompt(concept.text);
      setPrompt(prev => ({
        ...parsed,
        aspectRatio: prev.aspectRatio || parsed.aspectRatio || '1:1',
        textMedium: prev.textMedium || parsed.textMedium || '',
      }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load variation.';
      setError(msg);
    } finally {
      setLoadingVarIdx(null);
    }
  };

  const handleQuickFix = (type: 'flat' | 'anatomy' | 'generic' | 'background' | 'editorial' | 'transparent' | 'textshape') => {
    setPrompt(prev => {
      const next = { ...prev };
      switch (type) {
        case 'flat':
          next.lighting = (next.lighting ? next.lighting + ', ' : '') + 'rim light, volumetric haze, high contrast, cinematic depth';
          break;
        case 'anatomy':
          next.negatives = (next.negatives ? next.negatives + ', ' : '') + 'NO extra fingers, NO fused hands, NO distorted anatomy, NO warped geometry';
          next.camera = (next.camera ? next.camera + ', ' : '') + 'hands out of frame or in pockets';
          break;
        case 'generic':
          next.detail = (next.detail ? next.detail + ', ' : '') + 'hyper-detailed micro textures, material realism';
          next.quality = (next.quality ? next.quality + ', ' : '') + '8k raw photo, sharp focus, photorealistic';
          break;
        case 'background':
          next.environment = (next.environment ? next.environment + ', ' : '') + 'minimalistic clean studio background, professional lighting, soft bokeh';
          break;
        case 'editorial':
          // Replace "professional" with "editorial" throughout style — higher visual register in Images 2.0
          next.style = next.style
            ? next.style.replace(/\bprofessional\b/gi, 'editorial') + (!/editorial/i.test(next.style) ? ', editorial aesthetic' : '')
            : 'editorial aesthetic';
          break;
        case 'transparent':
          next.environment = (next.environment ? next.environment + ', ' : '') + 'transparent PNG background, no background fill';
          next.negatives = (next.negatives ? next.negatives + ', ' : '') + 'NO background, NO backdrop';
          break;
        case 'textshape':
          // Wrap text in shape for more reliable rendering
          if (next.songTitle) {
            next.textMedium = (next.textMedium ? next.textMedium + ', ' : '') + 'text inside a dark horizontal banner with slight transparency';
          }
          break;
      }
      return next;
    });
  };

  const handleCopyToClipboard = () => {
    clipboardCopy(finalPromptString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Parse the flowing prompt into display segments for the output panel
  const outputSegments = useMemo(() => {
    if (!finalPromptString) return [];
    return finalPromptString.split('\n').map((line, i) => {
      // Detect segment type for styling
      if (i === 0) return { type: 'scene', label: 'Scene', content: line };
      if (line.startsWith('"')) return { type: 'text', label: 'Typography', content: line };
      if (line.startsWith('Compose as')) return { type: 'composition', label: 'Composition Lock', content: line };
      if (line.startsWith('[CHARACTER')) return { type: 'character', label: 'Character Lock', content: line };
      if (/^NO\s/i.test(line)) return { type: 'negatives', label: 'Constraints', content: line };
      // Remaining lines: style, lighting, camera, detail/quality
      const guesses = [
        { test: /\b(shot on|editorial|photograph|aesthetic|cinematic|illustration|watercolor|oil paint|digital art)\b/i, label: 'Style' },
        { test: /\b(light|lighting|shadow|rim|key light|fill|glow|gradient|tonal)\b/i, label: 'Lighting' },
        { test: /\b(lens|mm|f\/|aperture|angle|depth of field|focal|framing|composition)\b/i, label: 'Camera' },
        { test: /\b(texture|detail|material|8k|resolution|sharp|photorealistic|raw photo)\b/i, label: 'Material' },
      ];
      const match = guesses.find(g => g.test.test(line));
      return { type: 'technical', label: match?.label || 'Detail', content: line };
    });
  }, [finalPromptString]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 selection:bg-accent/30">
      {/* Navigation Header */}
      <header className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-[1900px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center shadow-lg shadow-accent/20">
              <PenTool className="text-slate-950" size={24} />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight uppercase">Architect <span className="text-accent">AI</span></h1>
              <p className="text-[11px] text-slate-400 font-bold uppercase tracking-[0.2em]">ChatGPT Images 2.0 Protocol</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setPrompt(INITIAL_PROMPT_STATE);
                setMasterInput('');
              }}
              leftIcon={<RotateCcw size={14} />}
              className="text-[11px] uppercase tracking-widest"
            >
              Reset System
            </Button>
            {draftId && (
              <Button
                variant={saved ? 'success' : 'secondary'}
                size="sm"
                onClick={handleSave}
                disabled={isSaving}
                leftIcon={saved ? <CheckCircle2 size={16} /> : <Save size={16} />}
                className="text-[11px] uppercase tracking-widest"
              >
                {saved ? 'Saved' : isSaving ? 'Saving...' : 'Save to Draft'}
              </Button>
            )}
            <Button
              variant={copied ? 'success' : 'primary'}
              size="sm"
              onClick={handleCopyToClipboard}
              leftIcon={copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
              className="shadow-xl shadow-accent/20"
            >
              {copied ? 'Copied' : 'Copy Prompt'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1900px] mx-auto p-6 grid grid-cols-1 xl:grid-cols-12 gap-8">

        {/* Left Column: Input Modules */}
        <div className="xl:col-span-7 space-y-8">

          {/* AI Master Deconstructor */}
          <section className="bg-slate-800 border border-slate-700/50 rounded-lg p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-accent opacity-20"></div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="text-accent" size={18} />
              <h2 className="text-sm font-bold text-slate-50 uppercase tracking-widest">Master Architect Module</h2>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".md,.txt"
                className="hidden"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={triggerFileUpload}
                leftIcon={<UploadCloud size={14} />}
                title="Upload Markdown Context"
                className="text-[11px] uppercase tracking-widest whitespace-nowrap"
              >
                Upload Context
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleParse}
                disabled={isParsing || !masterInput.trim()}
                isLoading={isParsing}
                loadingText="Deconstructing..."
                className="text-[11px] uppercase tracking-widest whitespace-nowrap"
              >
                Execute Analysis
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleGenerateVariations}
                disabled={isVarying || !masterInput.trim()}
                isLoading={isVarying}
                loadingText="Diverging..."
                leftIcon={<Wand2 size={14} />}
                title="Generate 3 diverse concept directions from song context"
                className="text-[11px] uppercase tracking-widest whitespace-nowrap"
              >
                Generate 3 Variations
              </Button>
            </div>
            <TextArea
              size="md"
              className="bg-slate-950 min-h-[140px]"
              placeholder="Input core concept or upload a Markdown file with context (e.g. 'Synthesizer dreams of electric sheep, 1970s sci-fi film aesthetic')..."
              value={masterInput}
              onChange={(e) => setMasterInput(e.target.value)}
            />

            {/* Variation cards */}
            {variations.length > 0 && (
              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-2">
                  <Wand2 size={14} className="text-accent" />
                  <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
                    3 Divergent Concepts — pick one to deconstruct into fields
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {variations.map((concept, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg border border-slate-700/60 bg-slate-900/60 p-4 flex flex-col"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-black uppercase tracking-widest text-accent">
                          Concept {idx + 1}
                        </span>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed line-clamp-6 flex-1 mb-3 whitespace-pre-wrap">
                        {concept.text}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleLoadVariation(idx, concept)}
                        disabled={loadingVarIdx !== null}
                        isLoading={loadingVarIdx === idx}
                        loadingText="Loading..."
                        rightIcon={<ArrowRight size={12} />}
                        className="text-[10px] uppercase tracking-widest w-full"
                      >
                        Load into Fields
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Album Design Overlays + Aspect Ratio + Text Medium */}
          <section className="bg-slate-800/40 border border-accent/10 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Music className="text-accent" size={18} />
              <h2 className="text-sm font-bold text-slate-50 uppercase tracking-widest">Album Design & Format</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Album / Song Title</label>
                <Input
                  size="md"
                  value={prompt.songTitle}
                  onChange={(e) => handleFieldChange('songTitle', e.target.value)}
                  placeholder="TITLE TEXT"
                  className="bg-slate-950"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Artist Name</label>
                <Input
                  size="md"
                  value={prompt.artistName}
                  onChange={(e) => handleFieldChange('artistName', e.target.value)}
                  placeholder="ARTIST IDENTITY"
                  className="bg-slate-950"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                  <Ratio size={12} className="inline mr-1.5 text-accent" />
                  Aspect Ratio
                </label>
                <Select
                  size="md"
                  value={prompt.aspectRatio}
                  onChange={(e) => handleFieldChange('aspectRatio', e.target.value)}
                  options={ASPECT_RATIO_OPTIONS}
                  className="bg-slate-950"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">
                  Text Treatment
                </label>
                <Select
                  size="md"
                  value={prompt.textMedium}
                  onChange={(e) => handleFieldChange('textMedium', e.target.value)}
                  options={TEXT_MEDIUM_OPTIONS}
                  className="bg-slate-950"
                />
              </div>
            </div>
          </section>

          {/* Framework Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-8">
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-2 h-6 bg-accent"></div>
                <h2 className="font-bold text-xs uppercase tracking-[0.2em] text-slate-400">01 / Story Logic</h2>
              </div>
              <div className="space-y-4">
                {storyGroup.map(cat => (
                  <PromptField key={cat.id} {...cat} value={(prompt as any)[cat.id]} onChange={handleFieldChange} />
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-2 h-6 bg-accent"></div>
                <h2 className="font-bold text-xs uppercase tracking-[0.2em] text-slate-400">02 / Technical Optics</h2>
              </div>
              <div className="space-y-4">
                {shotGroup.map(cat => (
                  <PromptField key={cat.id} {...cat} value={(prompt as any)[cat.id]} onChange={handleFieldChange} />
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-2 h-6 bg-accent"></div>
                <h2 className="font-bold text-xs uppercase tracking-[0.2em] text-slate-400">03 / Material Surface</h2>
              </div>
              <div className="space-y-4">
                {realismGroup.map(cat => (
                  <PromptField key={cat.id} {...cat} value={(prompt as any)[cat.id]} onChange={handleFieldChange} />
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-2 h-6 bg-red-700"></div>
                <h2 className="font-bold text-xs uppercase tracking-[0.2em] text-slate-400">04 / Logic Constraints</h2>
              </div>
              <div className="space-y-4">
                {guardrailsGroup.map(cat => (
                  <PromptField key={cat.id} {...cat} value={(prompt as any)[cat.id]} onChange={handleFieldChange} />
                ))}
              </div>

              <div className="p-5 bg-slate-800 border border-slate-700 rounded-lg">
                <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-4">Optimization Protocols</h3>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => handleQuickFix('flat')}
                    className="text-[11px] font-bold hover:border-accent hover:text-accent"
                  >
                    DEEPEN LIGHT
                  </Button>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => handleQuickFix('anatomy')}
                    className="text-[11px] font-bold hover:border-red-500 hover:text-red-500"
                  >
                    FIX BIOMETRY
                  </Button>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => handleQuickFix('generic')}
                    className="text-[11px] font-bold hover:border-cyan-500 hover:text-cyan-500"
                  >
                    ULTRA REALISM
                  </Button>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => handleQuickFix('background')}
                    className="text-[11px] font-bold hover:border-accent hover:text-accent"
                  >
                    CLEAN SPACE
                  </Button>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => handleQuickFix('editorial')}
                    className="text-[11px] font-bold hover:border-emerald-500 hover:text-emerald-500"
                  >
                    EDITORIAL MODE
                  </Button>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => handleQuickFix('transparent')}
                    className="text-[11px] font-bold hover:border-violet-500 hover:text-violet-500"
                  >
                    TRANSPARENT BG
                  </Button>
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => handleQuickFix('textshape')}
                    className="text-[11px] font-bold hover:border-sky-500 hover:text-sky-500 col-span-2"
                  >
                    TEXT IN BANNER
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Prompt Output Panel */}
        <div className="xl:col-span-5">
          <div className="sticky top-24 space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden shadow-2xl flex flex-col min-h-[600px]">
              <div className="p-5 border-b border-slate-700 bg-slate-900/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="text-accent" size={16} />
                  <div>
                    <h3 className="text-xs font-bold text-slate-50 uppercase tracking-widest">Protocol Output</h3>
                    <p className="text-[11px] text-slate-400 uppercase">Images 2.0 Optimized</p>
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="xs"
                  onClick={handleCopyToClipboard}
                  leftIcon={<Copy size={12} />}
                  className="text-[11px] uppercase"
                >
                  Copy Protocol
                </Button>
              </div>

              <div className="flex-1 p-8 overflow-y-auto bg-slate-950/40 font-mono text-[13px] leading-relaxed space-y-5">
                {outputSegments.length > 0 ? (
                  outputSegments.map((seg, i) => {
                    const labelColors: Record<string, string> = {
                      scene: 'text-accent',
                      text: 'text-emerald-400',
                      composition: 'text-sky-400',
                      character: 'text-violet-400',
                      negatives: 'text-red-400',
                      technical: 'text-slate-400',
                    };
                    const dotColors: Record<string, string> = {
                      scene: 'bg-accent',
                      text: 'bg-emerald-400',
                      composition: 'bg-sky-400',
                      character: 'bg-violet-400',
                      negatives: 'bg-red-400',
                      technical: 'bg-slate-500',
                    };
                    return (
                      <div key={i} className="group flex flex-col gap-1 animate-in fade-in slide-in-from-bottom-1 duration-300">
                        <span className={`${labelColors[seg.type] || 'text-slate-400'} font-bold text-[11px] uppercase tracking-widest flex items-center gap-1.5`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${dotColors[seg.type] || 'bg-slate-500'}`}></span>
                          {seg.label}
                        </span>
                        <span className="text-slate-300 pl-3 border-l border-slate-700 group-hover:border-accent transition-colors whitespace-pre-wrap">
                          {seg.content}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="h-full flex flex-col items-center justify-center opacity-30 text-center p-12">
                    <FileText className="mb-4 text-slate-400" size={48} strokeWidth={1} />
                    <p className="text-xs font-bold uppercase tracking-widest">Awaiting Framework Data</p>
                    <p className="text-[11px] mt-2 leading-relaxed max-w-[200px]">Initialize the Master Architect or populate framework modules to generate output.</p>
                  </div>
                )}
              </div>

              {/* Data Statistics Module */}
              <div className="p-5 bg-slate-900 border-t border-slate-700 grid grid-cols-3 gap-4">
                <div className="bg-slate-950 border border-slate-700 rounded-md p-3 flex flex-col items-center gap-1">
                  <Hash className="text-slate-400" size={14} />
                  <span className="text-slate-200 font-mono text-sm leading-none">{finalPromptString.length}</span>
                  <span className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">Chars</span>
                </div>
                <div className="bg-slate-950 border border-slate-700 rounded-md p-3 flex flex-col items-center gap-1">
                  <Layout className="text-slate-400" size={14} />
                  <span className="text-slate-200 font-mono text-sm leading-none">{finalPromptString.split('\n').filter(l => l.trim()).length}</span>
                  <span className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">Blocks</span>
                </div>
                <div className="bg-slate-950 border border-slate-700 rounded-md p-3 flex flex-col items-center gap-1">
                  <Ratio className="text-slate-400" size={14} />
                  <span className="text-slate-200 font-mono text-sm leading-none">{prompt.aspectRatio || '1:1'}</span>
                  <span className="text-[11px] text-slate-400 uppercase tracking-widest font-bold">Ratio</span>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-700 border border-red-500/50 rounded-md text-slate-50 flex items-start gap-3 animate-in slide-in-from-top-2">
                <AlertTriangle size={18} className="shrink-0" />
                <div className="space-y-0.5">
                  <p className="text-[11px] font-bold uppercase tracking-widest">Protocol Anomaly</p>
                  <p className="text-xs opacity-90">{error}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <footer className="mt-20 border-t border-slate-800 bg-slate-900 px-6 py-12">
        <div className="max-w-[1900px] mx-auto flex flex-col md:row justify-between items-center gap-8">
          <div className="flex items-center gap-4">
             <div className="w-8 h-8 bg-slate-800 rounded-md border border-slate-700 flex items-center justify-center">
               <PenTool className="text-accent" size={16} />
             </div>
             <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">
               ChatGPT Images 2.0 Prompt Protocol • v3.0
             </p>
          </div>
          <div className="text-[11px] font-bold text-slate-700 uppercase tracking-[0.4em]">
             Architect AI System Operational • No Anomalies Detected
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PromptArchitectApp;
